import { cn } from "@core-ui/utils/cn";
import { NodeViewContextableWrapper } from "@ext/markdown/core/element/NodeViewContextableWrapper";
import { useBlockToolbar } from "@ext/markdown/elements/article/edit/helpers/useBlockToolbar";
import CodeBlockActions from "@ext/markdown/elements/codeBlockLowlight/edit/component/CodeBlockActions";
import { checkLanguage, loadLanguage } from "@ext/markdown/elements/codeBlockLowlight/edit/logic/Lowlight";
import {
	getLowerLangName,
	getOriginalLangName,
	getStandardCaseByLower,
} from "@ext/markdown/elements/codeBlockLowlight/edit/logic/LowlightLangs";
import StyledCodeBlock from "@ext/markdown/elements/codeBlockLowlight/render/component/StyledCodeBlock";
import BlockCommentView from "@ext/markdown/elements/comment/edit/components/View/BlockCommentView";
import type { Editor, NodeViewProps } from "@tiptap/core";
import type { Attrs } from "@tiptap/pm/model";
import { NodeViewContent } from "@tiptap/react";
import { useLayoutEffect, useRef, useState } from "react";
import { CodeBlockCopyButton } from "../../render/component/CodeBlockCopyButton";

export const useLowlightActions = (props: {
	language?: string;
	updateAttributes?: (props: Attrs) => void;
	editor?: Editor;
}) => {
	const { language, updateAttributes, editor } = props;
	const [isRegistered, setIsRegistered] = useState(false);

	const internalLogic = async (lang: string) => {
		const langRegistered = checkLanguage(lang);
		if (langRegistered) {
			setIsRegistered(true);
			forceUpdate();
			return;
		}

		const res = await loadLanguage(lang);
		if (res?.registered(lang)) setIsRegistered(true);
		forceUpdate();
	};

	const forceUpdate = () => {
		if (!editor || editor.isDestroyed || !editor.view) return;
		editor.view.dispatch(editor.view.state.tr.setMeta("forceUpdate", true));
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	useLayoutEffect(() => {
		const lowerLang = getLowerLangName(language);
		void internalLogic(lowerLang);
	}, [language]);

	const onChange = (lang: string) => {
		const lowerLang = getLowerLangName(lang);
		updateAttributes?.({ language: lowerLang });
		void internalLogic(lowerLang);
	};

	return { onChange, isRegistered };
};

const CodeBlockComponent = (props: NodeViewProps) => {
	const { node, editor, updateAttributes } = props;
	const viewWrapperRef = useRef<HTMLDivElement>(null);
	const isEditable = editor.isEditable;

	const { onChange } = useLowlightActions({
		language: getOriginalLangName(getStandardCaseByLower(node.attrs.language)),
		updateAttributes,
		editor,
	});

	useBlockToolbar(props, isEditable && <CodeBlockActions node={node} onChange={onChange} />, { comment: true });

	return (
		<NodeViewContextableWrapper props={props} ref={viewWrapperRef}>
			<BlockCommentView className="rounded-lg" commentId={node.attrs?.comment?.id}>
				<StyledCodeBlock className="group/code" spellCheck={false}>
					<CodeBlockCopyButton text={node.textContent} />
					<NodeViewContent
						className={cn(
							"[white-space:unset!important]",
							"[&>div:first-of-type]:overflow-auto",
							"[&>div:first-of-type]:px-[1.625em]",
							"[&>div:first-of-type]:py-[1.375em]",
						)}
					/>
				</StyledCodeBlock>
			</BlockCommentView>
		</NodeViewContextableWrapper>
	);
};

export default CodeBlockComponent;
